import React, { createContext, useContext, useEffect, useState } from "react";
import client, { setToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If returning from Google OAuth callback, let AuthCallback handle it.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("cl_token");
    if (!token) {
      setLoading(false);
      return;
    }
    client
      .get("/auth/me")
      .then((res) => setUser(res.data.user))
      .catch(() => {
        setToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const res = await client.post("/auth/login", { email, password });
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (name, email, password, phone) => {
    const referral_code = localStorage.getItem("cl_ref") || "";
    const res = await client.post("/auth/register", { name, email, password, phone, referral_code });
    localStorage.removeItem("cl_ref");
    return res.data;
  };

  const verifyEmail = async (token) => {
    const res = await client.post("/auth/verify-email", { token });
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const resendVerification = async (email) => {
    const res = await client.post("/auth/resend-verification", { email });
    return res.data;
  };

  const forgotPassword = async (email) => {
    const res = await client.post("/auth/forgot-password", { email });
    return res.data;
  };

  const resetPassword = async (token, password) => {
    const res = await client.post("/auth/reset-password", { token, password });
    return res.data;
  };

  // Exchange Google session_id (from OAuth callback) for our app JWT.
  const googleSession = async (sessionId) => {
    const res = await client.post("/auth/google/session", { session_id: sessionId });
    setToken(res.data.token);
    setUser(res.data.user);
    const ref = localStorage.getItem("cl_ref");
    if (ref) {
      try { await client.post("/auth/attach-referral", { code: ref }); } catch (e) {}
      localStorage.removeItem("cl_ref");
    }
    return res.data.user;
  };

  const completeProfile = async (phone, name) => {
    const res = await client.post("/auth/complete-profile", { phone, name });
    setUser(res.data.user);
    return res.data.user;
  };

  const requestAlpha = async (plan, note) => {
    const res = await client.post("/alpha/request", { plan, note });
    setUser(res.data.user);
    return res.data.user;
  };

  const refresh = async () => {
    const res = await client.get("/auth/me");
    setUser(res.data.user);
    return res.data.user;
  };

  const setSession = (token, u) => { setToken(token); setUser(u); };

  const logout = () => {
    setToken(null);
    setUser(null);
  };

  const cats = user?.alpha_categories || [];
  const isAdmin = user?.role === "admin";
  const isAnalyst = user?.role === "analyst";
  const isStaff = isAdmin || user?.role === "publisher";
  const isEditor = isStaff || isAnalyst;
  const isAlpha = isAdmin || cats.length > 0;
  const hasCategory = (c) => isAdmin || cats.includes(c);

  return (
    <AuthContext.Provider
      value={{ user, setUser, setSession, loading, login, register, verifyEmail, resendVerification, forgotPassword, resetPassword, googleSession, completeProfile, requestAlpha, refresh, logout, isAlpha, isAdmin, isStaff, isAnalyst, isEditor, hasCategory }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
